/**
 * Модуль для роботи з Web Push Notifications
 */

import axios from 'axios';

// VAPID публічний ключ з .env файлу
const VAPID_PUBLIC_KEY = process.env.VUE_APP_VAPID_PUBLIC_KEY;

// Перевірка наявності VAPID ключа
if (!VAPID_PUBLIC_KEY || VAPID_PUBLIC_KEY === 'YOUR_VAPID_PUBLIC_KEY_HERE') {
    console.error('VAPID_PUBLIC_KEY не налаштований! Додайте VUE_APP_VAPID_PUBLIC_KEY у файл .env');
}

/**
 * Конвертує VAPID ключ з Base64 у Uint8Array
 * @param {string} base64String - VAPID ключ у форматі Base64
 * @returns {Uint8Array}
 */
function urlBase64ToUint8Array(base64String) {
    if (!base64String) {
        throw new Error('VAPID ключ не визначено!');
    }
    
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Перевіряє підтримку браузером Web Push
 * @returns {boolean}
 */
function isPushNotificationSupported() {
    return 'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window;
}

/**
 * Запитує дозвіл користувача на показ нотифікацій
 * @returns {Promise<NotificationPermission>}
 */
async function askUserPermission() {
    if (!isPushNotificationSupported()) {
        console.warn('Push notifications не підтримуються цим браузером');
        return 'denied';
    }

    const permission = await Notification.requestPermission();
    console.log('Дозвіл на нотифікації:', permission);
    return permission;
}

/**
 * Реєструє Service Worker
 * @returns {Promise<ServiceWorkerRegistration>}
 */
async function registerServiceWorker() {
    if (!isPushNotificationSupported()) {
        throw new Error('Push notifications не підтримуються');
    }

    try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
            scope: '/'
        });
        console.log('Service Worker зареєстровано:', registration);
        
        // Чекаємо поки Service Worker активується
        await navigator.serviceWorker.ready;
        console.log('Service Worker готовий');
        
        return registration;
    } catch (error) {
        console.error('Помилка реєстрації Service Worker:', error);
        throw error;
    }
}

/**
 * Створює Push Subscription
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<PushSubscription>}
 */
async function createPushSubscription(registration) {
    try {
        const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
        
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey
        });

        console.log('Push subscription створено:', subscription);
        return subscription;
    } catch (error) {
        console.error('Помилка створення push subscription:', error);
        throw error;
    }
}

/**
 * Відправляє subscription на backend
 * @param {PushSubscription} subscription
 * @returns {Promise<any>}
 */
async function sendSubscriptionToBackend(subscription) {
    try {
        const subscriptionData = JSON.parse(JSON.stringify(subscription));
        
        const response = await axios.post('save-subscription', subscriptionData, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token_default')}`
            }
        });

        console.log('Subscription збережено на backend:', response.data);
        return response.data;
    } catch (error) {
        console.error('Помилка відправки subscription на backend:', error);
        throw error;
    }
}

/**
 * Отримує поточну підписку користувача
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<PushSubscription|null>}
 */
async function getUserSubscription(registration) {
    try {
        const subscription = await registration.pushManager.getSubscription();
        return subscription;
    } catch (error) {
        console.error('Помилка отримання subscription:', error);
        return null;
    }
}

/**
 * Скасовує підписку користувача
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<{success: boolean, message: string}>}
 */
async function unsubscribeUser(registration) {
    try {
        const subscription = await getUserSubscription(registration);
        
        if (!subscription) {
            console.log('Підписка не знайдена в браузері');
            return { success: false, message: 'no_subscription' };
        }

        const subscriptionData = JSON.parse(JSON.stringify(subscription));
        console.log('Відправка на видалення:', {
            endpoint: subscriptionData.endpoint,
            fullData: subscriptionData
        });

        try {
            // Спочатку видаляємо на сервері
            const response = await axios.post('delete-subscription',
                subscriptionData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('auth_token_default')}`
                    }
                }
            );
            
            console.log('Відповідь сервера:', response.data);
        } catch (serverError) {
            console.error('Помилка видалення на сервері:', serverError);
            if (serverError.response) {
                console.error('Деталі помилки:', serverError.response.data);
            }
            // Продовжуємо видалення в браузері навіть якщо помилка на сервері
        }
        
        // Видаляємо в браузері
        const unsubscribed = await subscription.unsubscribe();
        
        if (unsubscribed) {
            console.log('Підписка видалена в браузері');
            return { success: true, message: 'deleted' };
        } else {
            console.error('Не вдалося видалити підписку в браузері');
            return { success: false, message: 'browser_error' };
        }
        
    } catch (error) {
        console.error('Помилка відписки:', error);
        return { success: false, message: 'error' };
    }
}

/**
 * Оновлює push subscription (видаляє стару і створює нову з діалогом Allow/Block)
 * @returns {Promise<boolean>}
 */
async function refreshPushSubscription() {
    console.log('Оновлення Web Push Notifications...');

    if (!isPushNotificationSupported()) {
        console.warn('Web Push Notifications не підтримуються');
        return false;
    }

    try {
        // 1. Отримуємо/реєструємо Service Worker
        const registration = await registerServiceWorker();

        // 2. Видаляємо стару підписку (якщо є)
        const unsubResult = await unsubscribeUser(registration);
        if (unsubResult.success) {
            console.log('Стара підписка видалена');
        } else if (unsubResult.message === 'no_subscription') {
            console.log('Стара підписка не знайдена, створюємо нову');
        }

        // 3. Запитуємо дозвіл повторно (показує діалог Allow/Block)
        const permission = await askUserPermission();

        // 4. Якщо дозволено - створюємо нову підписку
        if (permission === 'granted') {
            const subscription = await createPushSubscription(registration);
            await sendSubscriptionToBackend(subscription);
            console.log('Нова підписка створена і збережена');
            return true;
        } else {
            console.log('Дозвіл на нотифікації не надано');
            return false;
        }
    } catch (error) {
        console.error('Помилка оновлення Web Push:', error);
        throw error;
    }
}

/**
 * Головна функція ініціалізації Web Push
 * @returns {Promise<void>}
 */
export async function initPushNotifications() {
    console.log('Ініціалізація Web Push Notifications...');

    // Перевірка підтримки
    if (!isPushNotificationSupported()) {
        console.warn('Web Push Notifications не підтримуються');
        return;
    }

    // Перевірка чи користувач авторизований
    const token = localStorage.getItem('auth_token_default');
    if (!token) {
        console.log('Користувач не авторизований, пропускаємо ініціалізацію push');
        return;
    }

    try {
        // Реєструємо Service Worker
        const registration = await registerServiceWorker();

        // Перевіряємо поточний статус дозволу
        if (Notification.permission === 'granted') {
            console.log('Дозвіл на нотифікації вже надано');
            
            // Перевіряємо чи є вже підписка
            let subscription = await getUserSubscription(registration);
            
            if (!subscription) {
                // Створюємо нову підписку
                subscription = await createPushSubscription(registration);
                await sendSubscriptionToBackend(subscription);
            } else {
                console.log('Підписка вже існує');
                // Можна оновити підписку на backend
                await sendSubscriptionToBackend(subscription);
            }
        } else if (Notification.permission !== 'denied') {
            // Запитуємо дозвіл якщо ще не запитували
            const permission = await askUserPermission();
            
            if (permission === 'granted') {
                const subscription = await createPushSubscription(registration);
                await sendSubscriptionToBackend(subscription);
            }
        } else {
            console.log('Дозвіл на нотифікації заборонено користувачем');
        }
    } catch (error) {
        console.error('Помилка ініціалізації Web Push:', error);
    }
}

/**
 * Експортуємо додаткові функції для використання в компонентах
 */
export {
    isPushNotificationSupported,
    askUserPermission,
    registerServiceWorker,
    getUserSubscription,
    unsubscribeUser,
    refreshPushSubscription
};

